import React, { useCallback, useRef, useState } from 'react';
import CancelSigningPreparationModal from './cancelSigningPreparationModal';
import { useDocumentState } from '../../../../contexts/DocumentContext';
import { cancelPreparingToSignDocument } from '../../../../api/mutations';
import { useMutation } from '@apollo/client';
import { DocumentStatusEnum } from '../../../../shared/constants/document';

import { useNotifications } from '../../../../notifications';
import { useNavigate, useParams } from 'react-router-dom';
import { fillPlaceholders, paths } from '../../../../routing';

interface CancelSigningPreparationModalContainerProps {
  show: boolean;
  onHide: () => void;
}

const CancelSigningPreparationModalContainer = ({ show, onHide }: CancelSigningPreparationModalContainerProps) => {
  const { state } = useDocumentState();
  const documentId = state.id;
  const [isLoading, setIsLoading] = useState(false);
  const additionalMessageInputRef = useRef<HTMLTextAreaElement>(null);
  const [cancelPreparingToSign] = useMutation(cancelPreparingToSignDocument);
  const navigate = useNavigate();
  const { teamId, dealId } = useParams();
  const [, { error: notifyError }] = useNotifications();

  const handleError = useCallback(() => {
    notifyError({
      message: 'Unable to cancel preparing to signing process.',
    });
  }, [notifyError]);

  const handleConfirm = useCallback(async () => {
    const additionalMessage = additionalMessageInputRef.current?.value;
    setIsLoading(true);
    cancelPreparingToSign({ variables: { documentId: documentId!, additionalMessage } })
      .then((response: any) => {
        const document = response?.data?.cancelPreparingToSignDocument;

        if (!document) {
          handleError();
          return;
        }

        const { status, receiverTeam } = document;
        const isSharedDocumentInReviewingStatus = receiverTeam && status === DocumentStatusEnum.REVIEWING;
        const isInternalDocumentInDraftStatus =
          !receiverTeam && (status === DocumentStatusEnum.DRAFT || status === DocumentStatusEnum.UPLOADED);

        if (
          !isSharedDocumentInReviewingStatus &&
          !isInternalDocumentInDraftStatus &&
          status !== DocumentStatusEnum.SHARED
        ) {
          handleError();
          return;
        }

        // Stop loading state before closing modal and redirecting
        setIsLoading(false);

        // Close modal
        onHide();

        // Navigate back to the edit document page with immediate redirect
        // Since we're doing a full page reload, no need for store updates or refetches
        const editDocumentUrl = fillPlaceholders(paths.Documents.document, {
          teamId: teamId!,
          dealId: dealId!,
          documentId: documentId!,
        });

        // Immediate redirect for better performance
        window.location.href = editDocumentUrl;
      })
      .catch(() => {
        handleError();
        setIsLoading(false);
        onHide();
      });
  }, [cancelPreparingToSign, handleError, onHide, documentId, navigate, teamId, dealId]);

  return (
    <CancelSigningPreparationModal
      onHide={onHide}
      show={show}
      onConfirm={handleConfirm}
      isLoading={isLoading}
      additionalMessageInputRef={additionalMessageInputRef}
    />
  );
};

export default CancelSigningPreparationModalContainer;
